import { useState } from 'react';
import { cn } from '@/lib/utils/cn';
import { Input } from '@/components/ui';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

export function SingleSelect({
  question,
  value,
  onChange,
  disabled,
  errors,
  displayOrder,
}: QuestionComponentProps<string | null>) {
  const [otherText, setOtherText] = useState('');
  const options = question.config?.options || [];
  const layout = question.config?.layout || 'vertical';
  const hasOtherOption = question.config?.hasOtherOption;
  const otherLabel = question.config?.otherOptionLabel || 'Other';

  // Apply display order if provided
  const orderedOptions = displayOrder
    ? displayOrder.map((i) => options[i]).filter(Boolean)
    : options;

  const handleSelect = (optionValue: string) => {
    if (disabled) return;
    onChange(optionValue);
  };

  const handleOtherChange = (text: string) => {
    setOtherText(text);
    onChange(`__other__:${text}`);
  };

  const isOtherSelected = typeof value === 'string' && value.startsWith('__other__:');
  const selectedValue = isOtherSelected ? '__other__' : value;

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div
        className={cn(
          'space-y-3',
          layout === 'horizontal' && 'flex flex-wrap gap-4 space-y-0',
          layout === 'grid' && `grid gap-3 grid-cols-${question.config?.gridColumns || 2}`
        )}
      >
        {orderedOptions.map((option) => (
          <label
            key={option.id}
            className={cn(
              'option-item',
              selectedValue === option.value && 'option-item-selected',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <input
              type="radio"
              name={question.code}
              value={option.value}
              checked={selectedValue === option.value}
              onChange={() => handleSelect(option.value)}
              disabled={disabled}
              className="h-5 w-5 border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-900">{option.text}</span>
          </label>
        ))}

        {/* Other option */}
        {hasOtherOption && (
          <div>
            <label
              className={cn(
                'option-item',
                isOtherSelected && 'option-item-selected',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                name={question.code}
                value="__other__"
                checked={isOtherSelected}
                onChange={() => handleOtherChange(otherText)}
                disabled={disabled}
                className="h-5 w-5 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-900">{otherLabel}</span>
            </label>
            {isOtherSelected && (
              <Input
                type="text"
                value={otherText}
                onChange={(e) => handleOtherChange(e.target.value)}
                placeholder="Please specify..."
                disabled={disabled}
                className="mt-2 ml-8"
              />
            )}
          </div>
        )}
      </div>
    </QuestionWrapper>
  );
}
